import React, { useEffect, useState, useCallback } from 'react';
import { t } from '../i18n';
import Layout from '../components/Layout';
import { integrationAPI, downloadStockMovementXlsx } from '../api';
import toast from 'react-hot-toast';

/* ТЗ §4: Отчёт по движению ТМЗ — количественный, для сверки бухгалтерией с 1С (ОСВ). */

const iso = (d) => d.toISOString().slice(0, 10);

const inputStyle = {
  padding: '8px 12px', background: 'var(--input)', border: '1px solid var(--border)',
  borderRadius: 8, color: 'var(--text)', fontSize: 13, outline: 'none',
};

const Card = ({ children, style = {} }) => (
  <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 18, ...style }}>
    {children}
  </div>
);

export default function StockMovementPage() {
  const today = new Date();
  const weekAgo = new Date(Date.now() - 7 * 864e5);
  const [dateFrom, setDateFrom] = useState(iso(weekAgo));
  const [dateTo, setDateTo] = useState(iso(today));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Сверяют по одному складу, а не по заводу целиком: в 1С отчёт берут именно
  // так. Не выбран — показываем все, но разложенными по складам, иначе строки
  // четырёх складов лежат вперемешку и сверить нельзя ни один.
  const [wh, setWh] = useState('');
  // Сверка почти всегда упирается в одну позицию: «почему по этой плите у меня
  // другое число». Листать ради неё шестьсот строк — та же работа, что руками.
  const [q, setQ] = useState('');
  // Свод — то, чем сверяют: «было, пришло, ушло, стало», одна строка на
  // позицию. Список документов отвечает на другой вопрос — «что происходило»,
  // и нужен, когда свод уже показал, ГДЕ разошлось. Поэтому свод по умолчанию.
  const [mode, setMode] = useState('sum');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await integrationAPI.getStockMovement({
        date_from: dateFrom, date_to: dateTo,
        warehouse: wh || undefined, q: q.trim() || undefined, mode,
      });
      setData(data);
    } catch (e) {
      toast.error(t("Не удалось загрузить отчёт"));
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, wh, q, mode]);

  useEffect(() => { load(); }, [load]);

  const num = (v) => new Intl.NumberFormat('ru-RU').format(v);

  return (
    <Layout title={t("Что пришло и ушло")}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{t('С даты')}</label>
            <input type="date" style={inputStyle} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{t('По дату')}</label>
            <input type="date" style={inputStyle} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div>
            <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{t('Вид')}</label>
            <div style={{ display: 'flex', gap: 0 }}>
              {[['sum', 'Свод по номенклатуре'], ['doc', 'По документам']].map(([k, label], i) => (
                <button key={k} onClick={() => setMode(k)} style={{
                  ...inputStyle, cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap',
                  background: mode === k ? 'var(--accent)' : 'var(--input)',
                  color: mode === k ? '#fff' : 'var(--muted)',
                  border: '1px solid ' + (mode === k ? 'var(--accent)' : 'var(--border)'),
                  borderRadius: i === 0 ? '8px 0 0 8px' : '0 8px 8px 0',
                }}>{t(label)}</button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{t('Склад')}</label>
            <select style={{ ...inputStyle, minWidth: 210 }} value={wh} onChange={(e) => setWh(e.target.value)}>
              <option value="">{t('— все склады, по группам —')}</option>
              {(data?.warehouses || []).map(w => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: '1 1 230px', minWidth: 200 }}>
            <label style={{ display: 'block', color: 'var(--muted)', fontSize: 12, marginBottom: 4 }}>{t('Номенклатура')}</label>
            <input style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              placeholder={t('Название, короткое имя или артикул…')} />
          </div>
          <button onClick={load} disabled={loading} style={{
            ...inputStyle, cursor: 'pointer', fontWeight: 700, background: 'var(--accent)', color: '#fff', border: 'none',
          }}>{loading ? '...' : 'Сформировать'}</button>
          <button onClick={() => downloadStockMovementXlsx({
            date_from: dateFrom, date_to: dateTo,
            warehouse: wh || undefined, q: q.trim() || undefined, mode,
          })} style={{
            ...inputStyle, cursor: 'pointer', fontWeight: 600,
          }}>📥 Excel</button>
          <div style={{ marginLeft: 'auto', color: 'var(--dim)', fontSize: 12, maxWidth: 380 }}>
            Количественный отчёт по всем приходам, расходам и перемещениям — для сверки с 1С
            (Оборотно-сальдовая ведомость, отчёт по складу).
          </div>
        </div>

        {data && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {/* Начальный и конечный — по краям, как в оборотно-сальдовой:
                отчёт отвечал «сколько двигалось», а сверяют по «сколько стало» */}
            <Card style={{ flex: 1, minWidth: 150 }}>
              <div style={{ color: 'var(--dim)', fontSize: 12 }}>{t('Остаток на начало')}</div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{num(data.opening)}</div>
            </Card>
            <Card style={{ flex: 1, minWidth: 150 }}>
              <div style={{ color: 'var(--dim)', fontSize: 12 }}>{t('Приход (＋)')}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--success, #22c55e)' }}>{num(data.total_in)}</div>
            </Card>
            <Card style={{ flex: 1, minWidth: 150 }}>
              <div style={{ color: 'var(--dim)', fontSize: 12 }}>{t('Расход (－)')}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--danger, #ef4444)' }}>{num(data.total_out)}</div>
            </Card>
            <Card style={{ flex: 1, minWidth: 150, borderColor: 'var(--accent)' }}>
              <div style={{ color: 'var(--dim)', fontSize: 12 }}>{t('Остаток на конец')}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: 'var(--accent)' }}>{num(data.closing)}</div>
            </Card>
            <Card style={{ flex: 1, minWidth: 130 }}>
              <div style={{ color: 'var(--dim)', fontSize: 12 }}>{t('Записей')}</div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{num(data.count)}</div>
            </Card>
          </div>
        )}

        {/* Склад — своим блоком, как в оборотно-сальдовой: шапка с остатком на
            начало, движения, остаток на конец. Сплошным списком сверить нельзя:
            строки четырёх складов лежат вперемешку, и ни один не сходится. */}
        {data && (data.groups || []).length === 0 && (
          <Card><div style={{ padding: 20, textAlign: 'center', color: 'var(--dim)' }}>
            {t('За период движений нет')}
          </div></Card>
        )}

        {(data?.groups || []).map((g) => (
          <Card key={g.warehouse_id || 'none'} style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
              padding: '12px 18px', background: 'var(--bg)',
              borderBottom: '1px solid var(--border)',
            }}>
              <div style={{ color: 'var(--text)', fontWeight: 800, fontSize: 15, flex: 1, minWidth: 180 }}>
                {g.warehouse}
                <span style={{ color: 'var(--dim)', fontWeight: 500, fontSize: 12, marginLeft: 8 }}>
                  {g.count} {t('записей')}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12.5 }}>
                <span style={{ color: 'var(--muted)' }}>
                  {t('на начало')}: <b style={{ color: 'var(--text)' }}>{num(g.opening)}</b>
                </span>
                <span style={{ color: 'var(--muted)' }}>
                  {t('приход')}: <b style={{ color: 'var(--success, #22c55e)' }}>{num(g.total_in)}</b>
                </span>
                <span style={{ color: 'var(--muted)' }}>
                  {t('расход')}: <b style={{ color: 'var(--danger, #ef4444)' }}>{num(g.total_out)}</b>
                </span>
                <span style={{ color: 'var(--muted)' }}>
                  {t('на конец')}: <b style={{ color: 'var(--accent)' }}>{num(g.closing)}</b>
                </span>
              </div>
            </div>

            {mode === 'sum' ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ color: 'var(--dim)', textAlign: 'left' }}>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{t('Номенклатура')}</th>
                      <th style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)' }}>{t('Ед.')}</th>
                      {['Было', 'Пришло', 'Ушло', 'Стало'].map((h) => (
                        <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', textAlign: 'right', whiteSpace: 'nowrap' }}>{t(h)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={r.nomenclature_id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '7px 10px', fontWeight: 600 }}>{r.nomenclature}</td>
                        <td style={{ padding: '7px 10px', color: 'var(--dim)' }}>{r.unit}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', color: 'var(--muted)' }}>{num(r.opening)}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', color: r.qty_in ? 'var(--success, #22c55e)' : 'var(--dim)', fontWeight: r.qty_in ? 700 : 400 }}>{r.qty_in ? num(r.qty_in) : ''}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', color: r.qty_out ? 'var(--danger, #ef4444)' : 'var(--dim)', fontWeight: r.qty_out ? 700 : 400 }}>{r.qty_out ? num(r.qty_out) : ''}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 800, color: r.closing < 0 ? 'var(--danger, #ef4444)' : 'var(--text)' }}>{num(r.closing)}</td>
                      </tr>
                    ))}
                    {g.rows.length === 0 && (
                      <tr><td colSpan={6} style={{ padding: 14, textAlign: 'center', color: 'var(--dim)' }}>
                        {t('Пусто')}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ color: 'var(--dim)', textAlign: 'left' }}>
                    {['Дата', 'Номенклатура', 'Ед.', 'Приход', 'Расход', 'Операция', 'Документ / примечание'].map((h) => (
                      <th key={h} style={{ padding: '8px 10px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '7px 10px', whiteSpace: 'nowrap', color: 'var(--muted)' }}>{r.date}</td>
                      <td style={{ padding: '7px 10px', fontWeight: 600 }}>{r.nomenclature}</td>
                      <td style={{ padding: '7px 10px', color: 'var(--dim)' }}>{r.unit}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', color: r.qty_in ? 'var(--success, #22c55e)' : 'var(--dim)', fontWeight: r.qty_in ? 700 : 400 }}>{r.qty_in ? num(r.qty_in) : ''}</td>
                      <td style={{ padding: '7px 10px', textAlign: 'right', color: r.qty_out ? 'var(--danger, #ef4444)' : 'var(--dim)', fontWeight: r.qty_out ? 700 : 400 }}>{r.qty_out ? num(r.qty_out) : ''}</td>
                      <td style={{ padding: '7px 10px', color: 'var(--muted)', whiteSpace: 'nowrap' }}>{r.source}</td>
                      <td style={{ padding: '7px 10px', color: 'var(--dim)', maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.doc}</td>
                    </tr>
                  ))}
                  {g.rows.length === 0 && (
                    <tr><td colSpan={7} style={{ padding: 14, textAlign: 'center', color: 'var(--dim)' }}>
                      {t('Движений за период не было — остаток не менялся')}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            )}
          </Card>
        ))}
      </div>
    </Layout>
  );
}
